import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { FILTER_TYPES } from '@openx/components-filters/lib/FiltersBar/types.ts';
import {
  PACKAGE_LIST_TABS,
  usePackageListTabs,
} from '@openx/components-package/components/PackagesList/hooks/usePackageListTabs.tsx';
import { PackagesList as PackagesListUI } from '@openx/components-package/components/PackagesList/PackagesList.tsx';
import { usePackageContext } from '@openx/components-package/utils/context/PackageContext.tsx';
import { useSharedPackageUsedByState } from '@openx/components-package/utils/hooks/useSharedPackageUsedByState.ts';
import { serverSideCriteriaProcessing } from '@openx/components-table/serverSideCriteriaProcessing.ts';
import { CuratedPackageAction, type PackageListItem, type PackagesListFiltersValue } from '@openx/types/package.ts';
import type { SelectOperation } from '@openx/types/selectableTable.ts';
import { type CriteriaChange, CriteriaDimension } from '@openx/types/tableCriteria.ts';
import { applySelectionChange } from '@openx/utils/lib/applySelectionChange.ts';
import { OPEN_AUDIENCE_ORG_ID } from '@openx/utils/services/config.ts';
import isEqual from 'lodash/isEqual';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useAuth } from '../../../../components/AuthContext/context.ts';
import { isAdmin } from '../../../../permissions/validators.ts';
import { useListCriteria } from '../hooks/useListCriteria.ts';
import { usePackagesRowActions } from '../hooks/usePackagesRowActions.tsx';
import { defaultCriteria } from './constants.ts';
import { useFiltersConfig } from './useFiltersConfig.ts';
import { criteriaChangeHandler } from './utils/utils.ts';

export type PackagesListProps = {
  selectedPackages: PackageListItem[];
  setSelectedPackages: Dispatch<SetStateAction<PackageListItem[]>>;
};

export const PackagesList = ({ selectedPackages, setSelectedPackages }: PackagesListProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { session } = useAuth();
  const isAdminAccount = isAdmin(session);
  const { activeTab, changeTab } = usePackageListTabs(location.pathname, true);
  const pendingTabRef = useRef(activeTab);

  const { criteria, setCriteria } = useListCriteria();
  const { usePackagePermissions } = usePackageContext();
  const { hasCuratedPermission, isInternalUser } = usePackagePermissions();

  const filtersConfig = useFiltersConfig();

  const changeFiltersHandler = useCallback(
    (filters: PackagesListFiltersValue) => {
      const newCriteria: CriteriaChange<CriteriaDimension, PackagesListFiltersValue> = {
        dimension: CriteriaDimension.FILTERS,
        value: filters,
      };
      criteriaChangeHandler({ criteria, newCriteria, setCriteria });
    },
    [criteria, setCriteria]
  );

  const queryCriteria = serverSideCriteriaProcessing(criteria);
  const organizationFilterConfig = filtersConfig.find(
    filter => filter.key === 'account_uid' && filter.type === FILTER_TYPES.AUTOCOMPLETE
  );
  const organizationOptions =
    organizationFilterConfig?.type === FILTER_TYPES.AUTOCOMPLETE ? organizationFilterConfig.options : undefined;
  const isOrganizationLoading =
    organizationFilterConfig?.type === FILTER_TYPES.AUTOCOMPLETE
      ? organizationFilterConfig.config?.isLoading
      : undefined;

  const changeSingleCriteriaDimensionHandler = useCallback(
    (newCriteria: CriteriaChange<CriteriaDimension, Record<string, unknown>>) => {
      criteriaChangeHandler({ criteria, newCriteria, setCriteria });
    },
    [criteria, setCriteria]
  );

  const handleSelectionChange = useCallback(
    (selectedItems: PackageListItem[], operation: SelectOperation) => {
      const selectedPackagesNext = applySelectionChange({
        changeItems: selectedItems,
        itemsComparer: isEqual,
        operation,
        previouslySelectedItems: selectedPackages,
      });
      setSelectedPackages(selectedPackagesNext);
    },
    [selectedPackages, setSelectedPackages]
  );

  const handlePageNumberCorrection = useCallback(
    (pageNumber: number) => {
      setCriteria(previousCriteria => ({
        ...previousCriteria,
        pagination: {
          ...(previousCriteria.pagination ?? defaultCriteria.pagination),
          pageNumber,
        },
      }));
    },
    [setCriteria]
  );

  const showCuratedTab = useMemo(
    () => hasCuratedPermission({ action: CuratedPackageAction.SHOW_LIST_TAB, pckg: { is_curated: true } }),
    [hasCuratedPermission]
  );

  const accountNetworkId = isAdminAccount
    ? undefined
    : showCuratedTab && pendingTabRef.current === PACKAGE_LIST_TABS.CURATED
      ? OPEN_AUDIENCE_ORG_ID
      : session?.account?.exchange_account_network_id || undefined;

  const hideUsedByColumn = !isInternalUser && activeTab === PACKAGE_LIST_TABS.CURATED;

  const { packageUsedBy } = useSharedPackageUsedByState(
    {
      criteria,
      criteriaGql: queryCriteria,
      accountNetworkId,
    },
    { enabled: !hideUsedByColumn }
  );
  const actions = usePackagesRowActions(packageUsedBy, activeTab === PACKAGE_LIST_TABS.CURATED);

  return (
    <>
      {showCuratedTab && (
        <Tabs
          data-test="packages-list-tabs"
          onChange={(event, value) => {
            pendingTabRef.current = value;
            setCriteria(defaultCriteria);
            changeTab(event, value);
          }}
          value={activeTab}
        >
          <Tab data-test="curated-packages-tab" label={t('Curated Packages')} value={PACKAGE_LIST_TABS.CURATED} />
          <Tab data-test="packages-tab" label={t('Packages')} value={PACKAGE_LIST_TABS.ALL} />
        </Tabs>
      )}

      <PackagesListUI
        actions={actions}
        changeFilters={changeFiltersHandler}
        changeSingleCriteriaDimension={changeSingleCriteriaDimensionHandler}
        toggleSelect={handleSelectionChange}
        criteriaObjectOa={{
          criteria,
          criteriaGql: queryCriteria,
          accountNetworkId,
          onPageNumberCorrection: handlePageNumberCorrection,
        }}
        showCuratedBadge={activeTab === PACKAGE_LIST_TABS.ALL}
        selectedPackages={selectedPackages}
        packageUsedBy={packageUsedBy}
        organizationOptions={organizationOptions}
        isOrganizationLoading={isOrganizationLoading}
      />
    </>
  );
};
